import type {
  ExpressCheckoutAvailablePaymentMethodsChangeEvent,
  ExpressCheckoutCancelEvent,
  ExpressCheckoutConfirmEvent,
  ExpressCheckoutElement,
  ExpressCheckoutElementEventName,
  ExpressCheckoutElementOptions,
  ExpressCheckoutElementUpdateOptions,
  ExpressCheckoutClickEvent,
  ExpressCheckoutClickResolveOptions,
  ExpressCheckoutShippingAddressChangeEvent,
  ExpressCheckoutShippingRateChangeEvent,
  ExpressCheckoutShippingUpdate,
  ExpressCheckoutWallet,
  ElementsErrorEvent,
  ElementsResizeEvent,
} from '../types/elements';
import { Emitter } from '../utils/emitter';
import {
  ELEMENTS_COMPONENT_BOOTSTRAP_CHANNEL,
  ElementsComponentMessageType,
  type ElementsComponentConnectMessage,
  type ElementsComponentEventPayload,
  type ElementsApplePayPaymentRequest,
  isElementsComponentFrameReadyMessage,
  isElementsPreviewProgressEvent,
} from './controller-protocol';
import type { ElementsGroupContext } from './group';
import { ElementsActionShell } from './action-shell';
import {
  acquireApplePayModalObserver,
  ensureApplePaySDK,
  releaseApplePayModalObserver,
} from './apple-pay-sdk';
import { buildExpressCheckoutFrameUrl } from './frame-url';
import { withElementsFrameNavigationIdentity } from './frame-url';
import { loadGooglePayScript } from '../express-utils';
import { APPLE_PAY_BRIDGE_POLICY, selectApplePaySessionVersion } from '../apple-pay-policy';
import { generateId } from '../utils/generate-id';
import { ELEMENTS_PROTOCOL_TEXT_LIMIT, isBoundedProtocolString } from './protocol-validation';
import {
  registerDeveloperAssistantTarget,
  type DeveloperAssistantRegistration,
  type DeveloperAssistantComponentDisplayOptions,
} from './developer-assistant-registration';
import { isElementVisible } from './element-visibility';
import {
  stageAppearanceTransitionFrame,
  type AppearanceTransitionFrame,
  type PreparedAppearanceTransition,
} from './appearance-transition-frame';
import {
  launchWithProviderPopupRecovery,
  type ProviderPopupRecovery,
} from './google-pay-popup-recovery';

const MAX_IFRAME_HEIGHT = 4096;
const EXPRESS_PREVIEW_INITIALIZATION_TIMEOUT_MS = 10_000;
const WALLET_OVERLAY_FALLBACK_Z_INDEX = '2147483647';
const DEVELOPER_DISPLAY_OPTION_KEYS = [
  'paymentMethods',
  'layout',
  'buttonType',
  'buttonTheme',
  'buttonHeight',
  'buttonRadius',
] as const;

/**
 * Reviewable merchant-callback, layout, and payload limits for Express
 * Checkout. These are SDK behavior bounds, distinct from the cross-frame
 * transport bounds in protocol-validation.ts.
 */
const EXPRESS_CHECKOUT_ELEMENT_POLICY = {
  merchantConfirmationTimeoutMs: 60_000,
  merchantWalletUpdateTimeoutMs: 30_000,
  overlayAlignmentToleranceCssPixels: 0.5,
  buttonMinimumHeightCssPixels: 40,
  buttonMaximumHeightCssPixels: 64,
  buttonMaximumRadiusCssPixels: 24,
  layoutMaximumTrackCount: 4,
  optionRowCount: 64,
  optionRowLabelCharacters: 256,
  defaultCurrencyFractionDigits: 2,
  decimalRadix: 10,
} as const;

type WalletUpdateSettlementResult = {
  accepted: boolean;
  update?: ExpressCheckoutShippingUpdate;
  amount: number;
  currency: string;
};

type WalletUpdateSettlement = (result: WalletUpdateSettlementResult) => void;

type GooglePayPromiseSettlement = {
  requestId: string;
  resolve: (value: Record<string, unknown>) => void;
  timeout: ReturnType<typeof setTimeout>;
};

type GooglePayButtonTargetConfiguration = Extract<
  ElementsComponentEventPayload,
  { event: 'google_pay_button_target' }
>;

type InlineStyleSnapshot = {
  element: HTMLElement;
  properties: Array<{
    name: string;
    value: string;
    priority: string;
  }>;
};

const WALLET_OVERLAY_ANCESTOR_OVERRIDES = {
  // A transformed, contained, clipped, or isolated merchant ancestor can turn
  // the wallet's fixed iframe into a local viewport. Neutralize those contexts
  // only while the provider sheet is open, then restore every inline value.
  transform: 'none',
  filter: 'none',
  perspective: 'none',
  'backdrop-filter': 'none',
  '-webkit-backdrop-filter': 'none',
  contain: 'none',
  'container-type': 'normal',
  'will-change': 'auto',
  'clip-path': 'none',
  mask: 'none',
  '-webkit-mask': 'none',
  overflow: 'visible',
  isolation: 'auto',
  'mix-blend-mode': 'normal',
  opacity: '1',
  'z-index': 'auto',
  'pointer-events': 'auto',
} as const;

export class ExpressCheckoutElementImpl implements ExpressCheckoutElement {
  readonly componentId = generateId();
  readonly componentType = 'express_checkout' as const;
  componentGeneration = 0;
  private readonly emitter = new Emitter<ExpressCheckoutElementEventName>();
  private options: ExpressCheckoutElementOptions;
  private componentRevision = 0;
  private iframe: HTMLIFrameElement | null = null;
  private mounted = false;
  private connectedFrame: MessageEventSource | null = null;
  private destroyed = false;
  private ready = false;
  private developerAssistant: DeveloperAssistantRegistration | null = null;
  private pendingPreview: {
    resolve(transition: PreparedAppearanceTransition): void;
    reject(error: Error): void;
    timeoutId: ReturnType<typeof setTimeout>;
    nextOptions: ExpressCheckoutElementOptions;
    commitOptions: boolean;
    transition: AppearanceTransitionFrame;
    wasReady: boolean;
    settled: boolean;
    availablePaymentMethods?: ExpressCheckoutAvailablePaymentMethodsChangeEvent['availablePaymentMethods'];
  } | null = null;
  private previewTransitionActive = false;
  private activeAttempt: {
    requestId: string;
    wallet: ExpressCheckoutWallet;
    approved: boolean;
    actionId?: string;
  } | null = null;
  private activeOptions: ExpressCheckoutElementOptions | null = null;
  private applePaySession: any = null;
  private walletOverlayRestoreStyle: string | null = null;
  private walletOverlayAncestorStyles: InlineStyleSnapshot[] = [];
  private walletOverlayHeight: number | null = null;
  private applePayTimeout: ReturnType<typeof setTimeout> | null = null;
  private applePayModalObserverAcquired = false;
  private googlePayClient: any = null;
  private googlePayEnvironment: 'TEST' | 'PRODUCTION' | null = null;
  private googlePayRequestId: string | null = null;
  private googlePayProcessingRequestId: string | null = null;
  private googlePayPopupRecovery: ProviderPopupRecovery<unknown> | null = null;
  private readonly googlePayUpdates = new Map<string, GooglePayPromiseSettlement>();
  private googlePayAuthorization: GooglePayPromiseSettlement | null = null;
  private googlePayButtonTarget: GooglePayButtonTargetConfiguration | null = null;
  private googlePayButtonContainer: HTMLDivElement | null = null;
  private googlePayButtonGeneration = 0;
  private googlePayButtonPositionFrame: number | null = null;
  private googlePayButtonResizeObserver: ResizeObserver | null = null;
  private readonly googlePayButtonPositionHandler = (): void => {
    if (this.googlePayButtonPositionFrame !== null) return;
    this.googlePayButtonPositionFrame = window.requestAnimationFrame(() => {
      this.googlePayButtonPositionFrame = null;
      this.positionGooglePayButton();
    });
  };
  private readonly actionShell = new ElementsActionShell(
    this.group.hostedOrigin,
    (actionId) => this.cancelAction(actionId),
    (actionId, code, message, recoverable) => this.failAction(actionId, code, message, recoverable),
  );
  private readonly messageHandler = (event: MessageEvent): void => this.onWindowMessage(event);
  private readonly walletOverlayKeyHandler = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || this.walletOverlayRestoreStyle === null) return;
    event.preventDefault();
    event.stopPropagation();
    const attempt = this.activeAttempt;
    if (attempt) this.group.cancelComponentSubmission(this.componentId, attempt.requestId);
    this.setWalletOverlay(false);
  };

  constructor(
    private readonly group: ElementsGroupContext,
    options: ExpressCheckoutElementOptions = {},
  ) {
    this.options = validateOptions(options);
  }

  mount(target: string | HTMLElement): void {
    if (this.destroyed) {
      throw new Error('[Paypercut Elements] Cannot mount a destroyed Express Checkout Element.');
    }
    if (this.mounted) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error(
        '[Paypercut Elements] Express Checkout Element can only be mounted in a browser.',
      );
    }
    if (this.group.isDestroyed()) {
      throw new Error('[Paypercut Elements] Cannot mount an Element from a destroyed group.');
    }
    const container = resolveContainer(target);
    if (!this.applePayModalObserverAcquired) {
      acquireApplePayModalObserver();
      this.applePayModalObserverAcquired = true;
    }

    const iframe = this.createFrame();

    this.iframe = iframe;
    this.mounted = true;
    this.group.markComponentMounted();
    window.addEventListener('message', this.messageHandler);
    this.group.ensureController();
    container.appendChild(iframe);
    this.registerDeveloperAssistant();
  }

  on(
    event: 'ready',
    handler: (event: ExpressCheckoutAvailablePaymentMethodsChangeEvent) => void,
  ): () => void;
  on(event: 'resize', handler: (event: ElementsResizeEvent) => void): () => void;
  on(event: 'error', handler: (event: ElementsErrorEvent) => void): () => void;
  on(
    event: 'availablepaymentmethodschange',
    handler: (event: ExpressCheckoutAvailablePaymentMethodsChangeEvent) => void,
  ): () => void;
  on(event: 'click', handler: (event: ExpressCheckoutClickEvent) => void): () => void;
  on(
    event: 'shippingaddresschange',
    handler: (event: ExpressCheckoutShippingAddressChangeEvent) => void,
  ): () => void;
  on(
    event: 'shippingratechange',
    handler: (event: ExpressCheckoutShippingRateChangeEvent) => void,
  ): () => void;
  on(event: 'confirm', handler: (event: ExpressCheckoutConfirmEvent) => void): () => void;
  on(event: 'cancel', handler: (event: ExpressCheckoutCancelEvent) => void): () => void;
  on(event: ExpressCheckoutElementEventName, handler: Function): () => void {
    return this.emitter.on(event, handler);
  }

  update(options: ExpressCheckoutElementUpdateOptions): void {
    if (this.destroyed) {
      throw new Error('[Paypercut Elements] Cannot update a destroyed Express Checkout Element.');
    }
    if (this.activeAttempt) {
      throw new Error(
        '[Paypercut Elements] Cannot update Express Checkout while a wallet attempt is active.',
      );
    }
    const validated = validateElementUpdateOptions(options);
    this.options = validateOptions({ ...this.options, ...validated });
    if (this.componentRevision === Number.MAX_SAFE_INTEGER) {
      throw new Error('[Paypercut Elements] The component update revision limit has been reached.');
    }
    this.componentRevision += 1;
    this.group.updateExpressCheckoutComponent(
      this.componentId,
      this.componentRevision,
      this.getComponentOptions(),
    );
  }

  getComponentOptions(): ExpressCheckoutElementOptions {
    return structuredCloneSafe(this.pendingPreview?.nextOptions ?? this.options);
  }

  isMounted(): boolean {
    return this.mounted && !this.destroyed;
  }

  remountAppearance(): Promise<PreparedAppearanceTransition> {
    return this.preparePreviewFrame(this.options);
  }

  cancelAppearanceRemount(componentGeneration: number, error: Error): void {
    if (this.componentGeneration !== componentGeneration) return;
    this.rejectPendingPreview(error);
  }

  markAppearanceRecoveryFailed(): void {
    this.ready = false;
    this.previewTransitionActive = false;
    if (this.iframe) {
      this.iframe.inert = true;
      this.iframe.style.pointerEvents = 'none';
    }
    if (this.googlePayButtonContainer) {
      this.googlePayButtonContainer.style.visibility = 'hidden';
      this.googlePayButtonContainer.style.pointerEvents = 'none';
    }
    this.emitter.emit('error', {
      code: 'elements_appearance_preview_recovery_failed',
      message: 'Express Checkout could not restore its last Appearance.',
      recoverable: true,
    });
  }

  updateDeveloperDisplayOptions(update: DeveloperAssistantComponentDisplayOptions): Promise<void> {
    const options = validateOptions(mergeDeveloperDisplayOptions(this.options, update));
    return this.replacePreviewFrame(options, true);
  }

  replaceDeveloperDisplayOptions(
    replacement: DeveloperAssistantComponentDisplayOptions,
  ): Promise<void> {
    const commerceOptions = structuredCloneSafe(this.options);
    for (const key of DEVELOPER_DISPLAY_OPTION_KEYS) delete commerceOptions[key];
    return this.replacePreviewFrame(
      validateOptions({ ...commerceOptions, ...structuredCloneSafe(replacement) }),
      true,
    );
  }

  hasActiveSubmission(): boolean {
    return (
      this.activeAttempt !== null ||
      this.applePaySession !== null ||
      this.googlePayRequestId !== null
    );
  }

  hasActiveProviderSubmission(): boolean {
    const googlePayAwaitingMerchantApproval =
      this.googlePayRequestId !== null &&
      this.activeAttempt?.requestId === this.googlePayRequestId &&
      this.activeAttempt.approved === false;
    return (
      this.activeAttempt?.approved === true ||
      this.applePaySession !== null ||
      (this.googlePayRequestId !== null && !googlePayAwaitingMerchantApproval)
    );
  }

  handleControllerError(error: ElementsErrorEvent): void {
    if (this.destroyed) return;
    this.finalizeWalletAttempt({ cancelSubmission: true, skipApplePayCompletion: false });
    if (!error.recoverable) this.ready = false;
    this.emitter.emit('error', error);
  }

  handleControllerEvent(payload: ElementsComponentEventPayload): void {
    if (this.destroyed) return;
    const preview = this.pendingPreview;
    if (preview && !isElementsPreviewProgressEvent(payload)) {
      if (payload.event === 'error') {
        this.failPendingPreview(
          new Error('The replacement Express Checkout frame could not render.'),
        );
      }
      return;
    }
    switch (payload.event) {
      case 'ready':
        if (!this.ready) {
          this.ready = true;
          if (preview && preview === this.pendingPreview) {
            window.clearTimeout(preview.timeoutId);
            preview.availablePaymentMethods = { ...payload.availablePaymentMethods! };
            preview.resolve(this.prepareSettledPreviewTransition(preview));
            break;
          }
          this.emitter.emit('ready', {
            availablePaymentMethods: { ...payload.availablePaymentMethods! },
          });
        }
        break;
      case 'resize':
        this.resize(payload.height);
        break;
      case 'wallet_overlay':
        this.setWalletOverlay(payload.open);
        break;
      case 'error':
        this.handleControllerError(payload);
        break;
      case 'available_payment_methods_change':
        this.emitter.emit('availablepaymentmethodschange', {
          availablePaymentMethods: { ...payload.availablePaymentMethods },
        });
        break;
      case 'apple_pay_capabilities_request':
        void ensureApplePaySDK().then(
          () => {
            if (this.destroyed) return;
            this.group.sendApplePayCapabilities(this.componentId, this.canMakeApplePayPayments());
          },
          () => {
            if (this.destroyed) return;
            this.group.sendApplePayCapabilities(this.componentId, false);
          },
        );
        break;
      case 'apple_pay_start':
        if (this.activeAttempt?.requestId !== payload.requestId) break;
        this.startApplePay(payload.requestId, payload.request);
        break;
      case 'apple_pay_merchant_session':
        if (this.activeAttempt?.requestId !== payload.requestId || !this.applePaySession) break;
        this.clearApplePayTimeout();
        try {
          this.applePaySession.completeMerchantValidation(payload.merchantSession);
        } catch {
          this.completeApplePay(false);
          this.group.sendApplePayCanceled(this.componentId, payload.requestId);
        }
        break;
      case 'google_pay_capabilities_request':
        void this.resolveGooglePayCapabilities(payload.environment, payload.requestJson);
        break;
      case 'google_pay_button_target':
        void this.configureGooglePayButton(payload);
        break;
      case 'google_pay_start':
        if (this.activeAttempt?.requestId !== payload.requestId) break;
        this.startGooglePay(payload.requestId, payload.environment, payload.requestJson);
        break;
      case 'google_pay_data_changed_result':
        this.resolveGooglePayDataChanged(payload.requestId, payload.updateId, payload.resultJson);
        break;
      case 'google_pay_authorization_result':
        this.resolveGooglePayAuthorization(payload.requestId, payload.resultJson);
        break;
      case 'wallet_click':
        if (
          !this.group.beginWalletAttempt(
            this.componentId,
            payload.requestId,
            payload.wallet,
            this.iframe,
          )
        ) {
          this.group.cancelComponentSubmission(this.componentId, payload.requestId);
          break;
        }
        this.activeAttempt = {
          requestId: payload.requestId,
          wallet: payload.wallet,
          approved: false,
        };
        if (
          payload.wallet === 'google_pay' &&
          this.googlePayProcessingRequestId === payload.requestId
        ) {
          this.group.markWalletAttemptProcessing(
            this.componentId,
            payload.requestId,
            payload.wallet,
          );
        }
        this.activeOptions = null;
        this.emitWalletClick(payload.requestId, payload.wallet);
        break;
      case 'wallet_shipping_address_change':
        if (this.activeAttempt?.requestId !== payload.requestId) break;
        this.emitShippingAddressChange(
          payload.requestId,
          payload.updateId,
          payload.wallet,
          payload.address,
        );
        break;
      case 'wallet_shipping_rate_change':
        if (this.activeAttempt?.requestId !== payload.requestId) break;
        this.emitShippingRateChange(
          payload.requestId,
          payload.updateId,
          payload.wallet,
          payload.shippingRate,
        );
        break;
      case 'action_required': {
        if (this.activeAttempt?.requestId !== payload.requestId) break;
        this.activeAttempt.actionId = payload.action.id;
        this.group.hideWalletAttemptOverlay(this.componentId, payload.requestId);
        const actionChannel = new MessageChannel();
        this.group.attachActionChannel(
          this.componentId,
          payload.requestId,
          payload.action.id,
          actionChannel.port1,
        );
        this.actionShell.show(payload.action, actionChannel.port2);
        break;
      }
      case 'wallet_confirm':
        if (this.activeAttempt?.requestId !== payload.requestId) break;
        this.emitWalletConfirm(payload);
        break;
      case 'wallet_cancel':
        if (this.activeAttempt?.requestId !== payload.requestId) break;
        this.finalizeWalletAttempt({ requestId: payload.requestId });
        this.emitter.emit('cancel', { wallet: payload.wallet });
        break;
      default:
        break;
    }
  }

  private emitWalletConfirm(
    payload: Extract<ElementsComponentEventPayload, { event: 'wallet_confirm' }>,
  ): void {
    const requestId = payload.requestId;
    let settled = false;
    const settle = (succeeded: boolean, error?: { code?: string; message?: string }) => {
      if (settled || this.destroyed || this.activeAttempt?.requestId !== requestId) return;
      settled = true;
      window.clearTimeout(timeout);
      this.group.resolveWalletConfirmation(this.componentId, requestId, succeeded, error);
      if (payload.wallet === 'apple_pay') {
        this.completeApplePay(succeeded);
        this.finalizeWalletAttempt({ requestId, skipApplePayCompletion: true });
        return;
      }
      this.finalizeWalletAttempt({ requestId });
    };
    const timeout = window.setTimeout(
      () =>
        settle(false, {
          code: 'merchant_confirmation_timeout',
          message: 'The merchant did not finish the checkout in time.',
        }),
      EXPRESS_CHECKOUT_ELEMENT_POLICY.merchantConfirmationTimeoutMs,
    );
    const event: ExpressCheckoutConfirmEvent = {
      wallet: payload.wallet,
      paymentMethod: { type: 'payment_method', id: payload.paymentMethod.id },
      ...(payload.paymentMethodPreview
        ? { paymentMethodPreview: payload.paymentMethodPreview }
        : {}),
      ...(payload.billingDetails ? { billingDetails: payload.billingDetails } : {}),
      ...(payload.shippingAddress ? { shippingAddress: payload.shippingAddress } : {}),
      ...(payload.shippingRate ? { shippingRate: payload.shippingRate } : {}),
      complete: () => settle(true),
      paymentFailed: (error) => settle(false, error),
    };
    if (!this.emitter.hasListeners('confirm')) {
      const confirmError = {
        code: 'merchant_confirmation_handler_missing',
        message: 'The merchant did not register a confirmation handler.',
      };
      this.emitter.emit('error', {
        code: confirmError.code,
        message: confirmError.message,
        recoverable: false,
      });
      event.paymentFailed(confirmError);
      return;
    }
    try {
      this.emitter.emitAndThrow('confirm', event);
    } catch {
      const confirmError = {
        code: 'merchant_confirmation_handler_failed',
        message: 'The merchant confirmation handler threw an error.',
      };
      this.emitter.emit('error', {
        code: confirmError.code,
        message: confirmError.message,
        recoverable: false,
      });
      event.paymentFailed(confirmError);
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.rejectPendingPreview(
      new Error('Express Checkout was destroyed during Appearance Preview.'),
    );
    this.developerAssistant?.unregister();
    this.developerAssistant = null;
    this.ready = false;
    if (this.activeAttempt) {
      this.finalizeWalletAttempt({ cancelSubmission: true });
    }
    this.activeOptions = null;
    this.actionShell.destroy();
    this.setWalletOverlay(false);
    this.abortApplePay();
    this.clearGooglePayAttempt();
    this.destroyGooglePayButton();
    if (typeof window !== 'undefined') window.removeEventListener('message', this.messageHandler);
    if (this.applePayModalObserverAcquired) {
      releaseApplePayModalObserver();
      this.applePayModalObserverAcquired = false;
    }
    this.iframe?.remove();
    this.iframe = null;
    this.connectedFrame = null;
    this.mounted = false;
    this.emitter.clear();
    this.group.remove(this);
  }

  private createFrame(options: ExpressCheckoutElementOptions = this.options): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    iframe.id = `paypercut-express-checkout-element-${this.componentId}`;
    iframe.title = 'Express checkout';
    iframe.referrerPolicy = 'origin';
    iframe.src = withElementsFrameNavigationIdentity(
      buildExpressCheckoutFrameUrl({
        hostedOrigin: this.group.hostedOrigin,
        controllerId: this.group.controllerId,
        componentId: this.componentId,
        clientOptions: this.group.getClientOptions(),
        elementsOptions: this.group.getRenderableElementsOptions(),
        options,
        referrer: window.location.origin,
      }),
      generateId(),
    );
    iframe.allow = 'payment *';
    iframe.setAttribute('allowtransparency', 'true');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('role', 'presentation');
    iframe.setAttribute('scrolling', 'no');
    Object.assign(iframe.style, {
      width: '100%',
      maxWidth: '100%',
      minWidth: '0',
      height: '0',
      margin: '0',
      padding: '0',
      border: '0',
      display: 'block',
      overflow: 'hidden',
      colorScheme: 'light dark',
      transition: 'height 250ms ease-in-out, opacity 250ms ease-in-out',
      willChange: 'height, opacity',
    });
    return iframe;
  }

  private replacePreviewFrame(
    nextOptions: ExpressCheckoutElementOptions,
    commitOptions = false,
  ): Promise<void> {
    const previousOptions = structuredCloneSafe(this.options);
    return this.preparePreviewFrame(nextOptions, commitOptions).then(
      (transition) => transition.commit(),
      async (error: unknown) => {
        if (shouldSkipAppearanceRecovery(error)) throw error;
        if (commitOptions && !this.destroyed && this.mounted) {
          try {
            const recovery = await this.preparePreviewFrame(previousOptions);
            recovery.commit();
          } catch {
            this.markAppearanceRecoveryFailed();
          }
        }
        throw error;
      },
    );
  }

  private preparePreviewFrame(
    nextOptions: ExpressCheckoutElementOptions,
    commitOptions = false,
  ): Promise<PreparedAppearanceTransition> {
    if (this.hasActiveSubmission() || !this.group.isAppearancePreviewAvailable()) {
      return Promise.reject(appearancePreviewRefusedError());
    }
    this.rejectPendingPreview(
      skipAppearanceRecovery(new Error('A newer Express Checkout preview superseded this one.')),
    );
    const frame = this.iframe;
    const parent = frame?.parentNode;
    if (this.destroyed || !this.mounted || !frame || !parent) {
      return Promise.resolve({ commit: () => undefined, rollback: () => undefined });
    }
    const wasReady = this.ready;
    this.ready = false;
    this.componentGeneration += 1;
    let resolvePreview!: (transition: PreparedAppearanceTransition) => void;
    let rejectPreview!: (error: Error) => void;
    const promise = new Promise<PreparedAppearanceTransition>((resolve, reject) => {
      resolvePreview = resolve;
      rejectPreview = reject;
    });
    try {
      const replacement = this.createFrame(nextOptions);
      const transition = stageAppearanceTransitionFrame(frame, replacement);
      const pending = {
        resolve: resolvePreview,
        reject: rejectPreview,
        timeoutId: 0 as ReturnType<typeof setTimeout>,
        nextOptions: structuredCloneSafe(nextOptions),
        commitOptions,
        transition,
        wasReady,
        settled: false,
      };
      pending.timeoutId = window.setTimeout(() => {
        if (this.pendingPreview !== pending) return;
        this.failPendingPreview(
          new Error('The replacement Express Checkout frame did not become ready.'),
        );
      }, EXPRESS_PREVIEW_INITIALIZATION_TIMEOUT_MS);
      this.pendingPreview = pending;
      this.previewTransitionActive = true;
      this.iframe = replacement;
      if (this.googlePayButtonContainer) {
        this.googlePayButtonContainer.style.pointerEvents = 'none';
      }
      this.group.detachComponent(this);
    } catch (error) {
      this.iframe = frame;
      this.ready = wasReady;
      this.componentGeneration -= 1;
      rejectPreview(
        skipAppearanceRecovery(
          error instanceof Error
            ? error
            : new Error('Unable to replace the Express Checkout frame.'),
        ),
      );
    }
    return promise;
  }

  private rejectPendingPreview(error: Error): void {
    const pending = this.pendingPreview;
    if (!pending) return;
    this.pendingPreview = null;
    window.clearTimeout(pending.timeoutId);
    if (pending.settled) return;
    pending.settled = true;
    pending.transition.rollback();
    this.iframe = pending.transition.previous;
    this.ready = pending.wasReady;
    this.previewTransitionActive = false;
    this.positionGooglePayButton();
    pending.reject(error);
  }

  private failPendingPreview(error: Error): void {
    const pending = this.pendingPreview;
    if (!pending) return;
    this.rejectPendingPreview(error);
  }

  private prepareSettledPreviewTransition(
    pending: {
      nextOptions: ExpressCheckoutElementOptions;
      commitOptions: boolean;
      transition: AppearanceTransitionFrame;
      wasReady: boolean;
      settled: boolean;
      availablePaymentMethods?: ExpressCheckoutAvailablePaymentMethodsChangeEvent['availablePaymentMethods'];
    },
  ): PreparedAppearanceTransition {
    return {
      commit: () => {
        if (pending.settled) return;
        pending.settled = true;
        if (this.pendingPreview === pending) this.pendingPreview = null;
        pending.transition.commit();
        if (pending.commitOptions) this.options = structuredCloneSafe(pending.nextOptions);
        this.previewTransitionActive = false;
        this.positionGooglePayButton();
        const availablePaymentMethods = pending.availablePaymentMethods;
        queueMicrotask(() => {
          if (this.destroyed || !availablePaymentMethods) return;
          this.emitter.emit('ready', { availablePaymentMethods });
        });
      },
      rollback: () => {
        if (pending.settled) return;
        pending.settled = true;
        if (this.pendingPreview === pending) this.pendingPreview = null;
        pending.transition.rollback();
        this.iframe = pending.transition.previous;
        this.ready = pending.wasReady;
        this.previewTransitionActive = false;
        this.positionGooglePayButton();
      },
    };
  }

  private registerDeveloperAssistant(): void {
    if (this.group.getClientOptions().developerAssistant !== true) {
      return;
    }
    this.developerAssistant = registerDeveloperAssistantTarget({
      id: `${this.group.controllerId}:${this.componentId}:express-checkout`,
      configurationIdentity: this.group.configurationIdentity,
      appearanceScopeId: this.group.controllerId,
      componentDisplayScopeId: this.componentId,
      isVisible: () => isElementVisible(this.iframe?.parentElement ?? null),
      isAvailable: () => true,
      isAppearanceAvailable: () => this.isMounted(),
      canPreview: () => this.group.isAppearancePreviewAvailable(),
      getAppearance: () => this.group.getAppearance(),
      updateAppearance: (appearance) => this.group.updateAppearance(appearance),
      replaceAppearance: (appearance) => this.group.replaceAppearance(appearance),
      prepareAppearance: (appearance) => this.group.prepareAppearance(appearance),
      prepareAppearanceReplacement: (appearance) =>
        this.group.prepareAppearanceReplacement(appearance),
      getComponentDisplayOptions: () => developerDisplayOptions(this.options),
      updateComponentDisplayOptions: (options) => this.updateDeveloperDisplayOptions(options),
      replaceComponentDisplayOptions: (options) => this.replaceDeveloperDisplayOptions(options),
    });
  }

  private onWindowMessage(event: MessageEvent): void {
    if (
      this.destroyed ||
      !this.iframe?.contentWindow ||
      event.source !== this.iframe.contentWindow ||
      event.source === this.connectedFrame ||
      event.origin !== this.group.hostedOrigin ||
      !isElementsComponentFrameReadyMessage(event.data)
    ) {
      return;
    }

    const previousConnectedFrame = this.connectedFrame;
    const reinitialized = previousConnectedFrame !== null;
    this.connectedFrame = event.source;
    const channel = new MessageChannel();
    const message: ElementsComponentConnectMessage = {
      channel: ELEMENTS_COMPONENT_BOOTSTRAP_CHANNEL,
      type: ElementsComponentMessageType.Connect,
      controllerId: this.group.controllerId,
      componentId: this.componentId,
      componentType: this.componentType,
    };
    try {
      this.iframe.contentWindow.postMessage(message, this.group.hostedOrigin, [channel.port2]);
      if (reinitialized) this.group.reattachComponent(this, channel.port1);
      else this.group.attachComponent(this, channel.port1);
    } catch {
      this.connectedFrame = previousConnectedFrame;
      channel.port1.close();
      channel.port2.close();
      this.handleControllerError({
        code: 'elements_component_connect_failed',
        message: 'The express checkout component could not be connected.',
        recoverable: true,
      });
    }
  }

  private resize(height: number): void {
    if (!this.iframe || !Number.isFinite(height) || height < 0 || height > MAX_IFRAME_HEIGHT) {
      return;
    }
    const roundedHeight = Math.ceil(height);
    if (this.walletOverlayRestoreStyle !== null) {
      this.walletOverlayHeight = roundedHeight;
      return;
    }
    this.iframe.style.height = `${roundedHeight}px`;
    this.emitter.emit('resize', { height: roundedHeight });
  }

  private setWalletOverlay(open: boolean): void {
    const iframe = this.iframe;
    if (!iframe) return;
    if (open) {
      if (this.walletOverlayRestoreStyle !== null) return;
      this.walletOverlayRestoreStyle = iframe.style.cssText;
      document.addEventListener('keydown', this.walletOverlayKeyHandler, true);
      this.walletOverlayAncestorStyles = [];
      for (let ancestor = iframe.parentElement; ancestor; ancestor = ancestor.parentElement) {
        const properties = Object.entries(WALLET_OVERLAY_ANCESTOR_OVERRIDES).map(
          ([name, value]) => {
            const snapshot = {
              name,
              value: ancestor.style.getPropertyValue(name),
              priority: ancestor.style.getPropertyPriority(name),
            };
            ancestor.style.setProperty(name, value, 'important');
            return snapshot;
          },
        );
        this.walletOverlayAncestorStyles.push({ element: ancestor, properties });
      }
      const overlayStyles = {
        position: 'fixed',
        inset: '0',
        'z-index': WALLET_OVERLAY_FALLBACK_Z_INDEX,
        width: '100vw',
        'max-width': 'none',
        height: CSS.supports('height', '100dvh') ? '100dvh' : '100vh',
        margin: '0',
        'box-sizing': 'border-box',
        overflow: 'visible',
        transition: 'none',
        opacity: '1',
      } as const;
      for (const [name, value] of Object.entries(overlayStyles)) {
        iframe.style.setProperty(name, value, 'important');
      }
      const rect = iframe.getBoundingClientRect();
      if (
        Math.abs(rect.left) > EXPRESS_CHECKOUT_ELEMENT_POLICY.overlayAlignmentToleranceCssPixels ||
        Math.abs(rect.top) > EXPRESS_CHECKOUT_ELEMENT_POLICY.overlayAlignmentToleranceCssPixels
      ) {
        iframe.style.setProperty(
          'transform',
          `translate3d(${-rect.left}px, ${-rect.top}px, 0)`,
          'important',
        );
      }
      return;
    }
    if (this.walletOverlayRestoreStyle === null) return;
    iframe.style.cssText = this.walletOverlayRestoreStyle;
    this.walletOverlayRestoreStyle = null;
    document.removeEventListener('keydown', this.walletOverlayKeyHandler, true);
    for (const { element, properties } of this.walletOverlayAncestorStyles.reverse()) {
      for (const { name, value, priority } of properties) {
        if (value) element.style.setProperty(name, value, priority);
        else element.style.removeProperty(name);
      }
    }
    this.walletOverlayAncestorStyles = [];
    if (this.walletOverlayHeight !== null) {
      const height = this.walletOverlayHeight;
      this.walletOverlayHeight = null;
      this.resize(height);
    }
  }

  private emitWalletClick(requestId: string, wallet: ExpressCheckoutWallet): void {
    if (!this.emitter.hasListeners('click')) {
      if (this.activeAttempt?.requestId === requestId) {
        this.activeAttempt.approved = true;
      }
      this.activeOptions = structuredCloneSafe(this.options);
      this.group.resolveWalletClick(this.componentId, requestId, true, this.options);
      return;
    }

    let settled = false;
    const settle = (accepted: boolean, options?: ExpressCheckoutClickResolveOptions) => {
      if (settled || this.destroyed || this.activeAttempt?.requestId !== requestId) return;
      const validatedResolution = accepted ? validateClickResolveOptions(options) : undefined;
      const { amount, ...checkoutDetails } = validatedResolution ?? {};
      const validatedOptions =
        Object.keys(checkoutDetails).length > 0 ? checkoutDetails : undefined;
      settled = true;
      window.clearTimeout(timeout);
      if (accepted && this.activeAttempt?.requestId === requestId) {
        this.activeAttempt.approved = true;
        this.activeOptions = structuredCloneSafe({
          ...this.options,
          ...(validatedOptions ?? {}),
        });
      }
      if (amount === undefined) {
        this.group.resolveWalletClick(this.componentId, requestId, accepted, validatedOptions);
      } else {
        this.group.resolveWalletClick(
          this.componentId,
          requestId,
          accepted,
          validatedOptions,
          amount,
        );
      }
      if (!accepted) {
        this.group.endWalletAttempt(this.componentId, requestId);
        // A top-level Google Pay sheet is already open before the async
        // merchant click decision returns. Keep the request identity until the
        // controller's wallet_cancel arrives so integrations receive their
        // normal cancel lifecycle and can release their own attempt lock.
        if (
          wallet !== 'google_pay' ||
          this.googlePayRequestId !== requestId
        ) {
          this.activeAttempt = null;
          this.activeOptions = null;
        }
      }
    };
    // Woo and other commerce adapters may need to validate their checkout and
    // recalculate the cart before opening a native wallet sheet. Match the
    // shipping-update budget rather than rejecting a healthy integration after
    // one second on a slow network.
    const timeout = window.setTimeout(
      () => settle(false),
      EXPRESS_CHECKOUT_ELEMENT_POLICY.merchantWalletUpdateTimeoutMs,
    );
    const emitError = () => {
      const clickError = {
        code: 'merchant_click_handler_failed',
        message: 'The merchant click handler returned invalid data.',
      };
      this.emitter.emit('error', {
        code: clickError.code,
        message: clickError.message,
        recoverable: false,
      });
      settle(false);
    };
    try {
      this.emitter.emitAndThrow('click', {
        wallet,
        resolve: (options) => {
          try {
            settle(true, options);
          } catch {
            emitError();
          }
        },
        reject: () => settle(false),
      } satisfies ExpressCheckoutClickEvent);
    } catch {
      const clickError = {
        code: 'merchant_click_handler_failed',
        message: 'The merchant click handler threw an error.',
      };
      this.emitter.emit('error', {
        code: clickError.code,
        message: clickError.message,
        recoverable: false,
      });
      settle(false);
    }
  }

  private emitShippingAddressChange(
    requestId: string,
    updateId: string,
    wallet: ExpressCheckoutWallet,
    address: ExpressCheckoutShippingAddressChangeEvent['address'],
    onSettled?: WalletUpdateSettlement,
  ): void {
    if (!this.emitter.hasListeners('shippingaddresschange')) {
      this.group.resolveWalletUpdate(this.componentId, requestId, updateId, true);
      onSettled?.(this.walletUpdateSettlement(true));
      return;
    }
    this.emitShippingUpdate(
      'shippingaddresschange',
      requestId,
      updateId,
      {
        wallet,
        address,
      },
      onSettled,
    );
  }

  private emitShippingRateChange(
    requestId: string,
    updateId: string,
    wallet: ExpressCheckoutWallet,
    shippingRate: ExpressCheckoutShippingRateChangeEvent['shippingRate'],
    onSettled?: WalletUpdateSettlement,
  ): void {
    if (!this.emitter.hasListeners('shippingratechange')) {
      this.group.resolveWalletUpdate(this.componentId, requestId, updateId, true);
      onSettled?.(this.walletUpdateSettlement(true));
      return;
    }
    this.emitShippingUpdate(
      'shippingratechange',
      requestId,
      updateId,
      {
        wallet,
        shippingRate,
      },
      onSettled,
    );
  }

  private emitShippingUpdate(
    event: 'shippingaddresschange' | 'shippingratechange',
    requestId: string,
    updateId: string,
    payload:
      | Pick<ExpressCheckoutShippingAddressChangeEvent, 'wallet' | 'address'>
      | Pick<ExpressCheckoutShippingRateChangeEvent, 'wallet' | 'shippingRate'>,
    onSettled?: WalletUpdateSettlement,
  ): void {
    let settled = false;
    const settle = (accepted: boolean, update?: ExpressCheckoutShippingUpdate) => {
      if (settled || this.destroyed || this.activeAttempt?.requestId !== requestId) return;
      const validatedUpdate = accepted ? validateShippingUpdate(update) : undefined;
      settled = true;
      window.clearTimeout(timeout);
      if (accepted && validatedUpdate) {
        const hasLineItems = Object.prototype.hasOwnProperty.call(validatedUpdate, 'lineItems');
        const hasShippingRates = Object.prototype.hasOwnProperty.call(
          validatedUpdate,
          'shippingRates',
        );
        this.activeOptions = structuredCloneSafe({
          ...(this.activeOptions ?? this.options),
          ...(hasLineItems ? { lineItems: validatedUpdate.lineItems } : {}),
          ...(hasShippingRates ? { shippingRates: validatedUpdate.shippingRates } : {}),
        });
      }
      this.group.resolveWalletUpdate(
        this.componentId,
        requestId,
        updateId,
        accepted,
        validatedUpdate,
      );
      onSettled?.(this.walletUpdateSettlement(accepted, validatedUpdate));
    };
    const timeout = window.setTimeout(
      () => settle(false),
      EXPRESS_CHECKOUT_ELEMENT_POLICY.merchantWalletUpdateTimeoutMs,
    );
    const emitError = (errorMessage: string) => {
      this.emitter.emit('error', {
        code: 'merchant_shipping_update_handler_failed',
        message: errorMessage,
        recoverable: false,
      });
      settle(false);
    };
    try {
      this.emitter.emitAndThrow(event, {
        ...payload,
        resolve: (update?: ExpressCheckoutShippingUpdate) => {
          try {
            settle(true, update);
          } catch {
            emitError('The merchant shipping update handler returned invalid data.');
          }
        },
        reject: () => settle(false),
      });
    } catch {
      const shippingError = {
        code: 'merchant_shipping_update_handler_failed',
        message: 'The merchant shipping update handler threw an error.',
      };
      this.emitter.emit('error', {
        code: shippingError.code,
        message: shippingError.message,
        recoverable: false,
      });
      settle(false);
    }
  }

  private cancelAction(actionId: string): void {
    const attempt = this.activeAttempt;
    if (!attempt || attempt.actionId !== actionId) return;
    this.group.cancelComponentSubmission(this.componentId, attempt.requestId);
  }

  private failAction(actionId: string, code: string, message: string, recoverable: boolean): void {
    const attempt = this.activeAttempt;
    if (!attempt || attempt.actionId !== actionId) return;
    if (attempt) {
      this.finalizeWalletAttempt({ cancelSubmission: true });
    }
    this.emitter.emit('error', { code, message, recoverable });
  }

  private finalizeWalletAttempt(
    options: {
      requestId?: string;
      cancelSubmission?: boolean;
      skipApplePayCompletion?: boolean;
    } = {},
  ): void {
    const attempt = this.activeAttempt;
    if (!attempt || (options.requestId !== undefined && attempt.requestId !== options.requestId))
      return;
    this.group.endWalletAttempt(this.componentId, attempt.requestId);
    if (options.cancelSubmission) {
      this.group.cancelComponentSubmission(this.componentId, attempt.requestId);
    }
    if (!options.skipApplePayCompletion && attempt.wallet === 'apple_pay') {
      this.completeApplePay(false);
    }
    if (attempt.wallet === 'google_pay') {
      this.clearGooglePayAttempt(attempt.requestId);
    }
    this.activeAttempt = null;
    this.activeOptions = null;
    this.actionShell.close();
  }

  private canMakeApplePayPayments(): boolean {
    if (typeof window === 'undefined' || !window.isSecureContext) return false;
    const ApplePayAPI = (window as any).ApplePaySession;
    try {
      return Boolean(ApplePayAPI?.canMakePayments?.());
    } catch {
      return false;
    }
  }

  private async resolveGooglePayCapabilities(
    environment: 'TEST' | 'PRODUCTION',
    requestJson: string,
  ): Promise<void> {
    try {
      await loadGooglePayScript();
      if (this.destroyed) return;
      const request = parseJsonRecord(requestJson);
      const client = this.createGooglePayClient(environment);
      const result = await client.isReadyToPay(request);
      if (this.destroyed) return;
      this.googlePayClient = client;
      this.googlePayEnvironment = environment;
      this.group.sendGooglePayCapabilities(this.componentId, result?.result === true);
    } catch {
      if (!this.destroyed) this.group.sendGooglePayCapabilities(this.componentId, false);
    }
  }

  private async configureGooglePayButton(
    target: GooglePayButtonTargetConfiguration,
  ): Promise<void> {
    const generation = ++this.googlePayButtonGeneration;
    this.googlePayButtonTarget = target;
    if (!target.visible) {
      this.removeGooglePayButton();
      this.group.sendGooglePayCapabilities(this.componentId, false);
      return;
    }

    try {
      await loadGooglePayScript();
      if (this.destroyed || generation !== this.googlePayButtonGeneration) return;
      const request = parseJsonRecord(target.requestJson);
      const allowedPaymentMethods = request.allowedPaymentMethods;
      if (!Array.isArray(allowedPaymentMethods)) throw new Error('Invalid Google Pay request.');
      const client = this.createGooglePayClient(target.environment);
      const result = await client.isReadyToPay({
        apiVersion: request.apiVersion,
        apiVersionMinor: request.apiVersionMinor,
        allowedPaymentMethods,
      });
      if (this.destroyed || generation !== this.googlePayButtonGeneration) return;
      const available = result?.result === true;
      this.googlePayClient = client;
      this.googlePayEnvironment = target.environment;
      this.group.sendGooglePayCapabilities(this.componentId, available);
      if (!available) {
        this.removeGooglePayButton();
        return;
      }
      this.mountGooglePayButton(client, target, request, allowedPaymentMethods);
    } catch {
      if (this.destroyed || generation !== this.googlePayButtonGeneration) return;
      this.removeGooglePayButton();
      this.group.sendGooglePayCapabilities(this.componentId, false);
    }
  }

  private mountGooglePayButton(
    client: any,
    target: GooglePayButtonTargetConfiguration,
    request: Record<string, unknown>,
    allowedPaymentMethods: unknown[],
  ): void {
    this.removeGooglePayButton();
    const container = document.createElement('div');
    container.dataset.paypercutElementsGooglePayButton = this.componentId;
    Object.assign(container.style, {
      position: 'fixed',
      margin: '0',
      padding: '0',
      border: '0',
      overflow: 'hidden',
      zIndex: '2147483000',
      pointerEvents: 'auto',
      visibility: 'hidden',
    });

    const button = client.createButton({
      buttonColor: target.buttonColor,
      buttonType: target.buttonType,
      buttonRadius: target.buttonRadius,
      buttonSizeMode: 'fill',
      allowedPaymentMethods,
      onClick: () => {
        if (
          this.destroyed ||
          this.previewTransitionActive ||
          this.googlePayRequestId ||
          this.activeAttempt
        ) {
          return;
        }
        const requestId = generateId();
        container.style.visibility = 'hidden';
        container.style.pointerEvents = 'none';
        // The controller learns about the attempt, but it is deliberately not
        // awaited. Google Pay must launch directly from this top-level button's
        // trusted click handler or iOS loses popup/opener authorization.
        this.group.sendGooglePayClick(this.componentId, requestId);
        this.startGooglePay(requestId, target.environment, stringifyJsonRecord(request));
      },
    }) as HTMLElement;
    Object.assign(button.style, { width: '100%', height: '100%', margin: '0' });
    container.appendChild(button);
    document.body.appendChild(container);
    this.googlePayButtonContainer = container;
    window.addEventListener('resize', this.googlePayButtonPositionHandler);
    window.addEventListener('scroll', this.googlePayButtonPositionHandler, true);
    if (typeof ResizeObserver !== 'undefined' && this.iframe) {
      this.googlePayButtonResizeObserver = new ResizeObserver(this.googlePayButtonPositionHandler);
      this.googlePayButtonResizeObserver.observe(this.iframe);
    }
    this.positionGooglePayButton();
  }

  private positionGooglePayButton(): void {
    const container = this.googlePayButtonContainer;
    const target = this.googlePayButtonTarget;
    const iframe = this.iframe;
    if (
      !container ||
      !target?.visible ||
      !iframe ||
      this.previewTransitionActive ||
      this.googlePayRequestId
    ) {
      return;
    }
    const frameRect = iframe.getBoundingClientRect();
    const left = frameRect.left + target.target.x;
    const top = frameRect.top + target.target.y;
    Object.assign(container.style, {
      left: `${left}px`,
      top: `${top}px`,
      width: `${target.target.width}px`,
      height: `${target.target.height}px`,
      borderRadius: `${target.buttonRadius}px`,
      visibility:
        frameRect.width > 0 &&
        frameRect.height > 0 &&
        target.target.width > 0 &&
        target.target.height > 0
          ? 'visible'
          : 'hidden',
      pointerEvents: 'auto',
    });
  }

  private removeGooglePayButton(): void {
    if (this.googlePayButtonPositionFrame !== null) {
      window.cancelAnimationFrame(this.googlePayButtonPositionFrame);
      this.googlePayButtonPositionFrame = null;
    }
    window.removeEventListener('resize', this.googlePayButtonPositionHandler);
    window.removeEventListener('scroll', this.googlePayButtonPositionHandler, true);
    this.googlePayButtonResizeObserver?.disconnect();
    this.googlePayButtonResizeObserver = null;
    this.googlePayButtonContainer?.remove();
    this.googlePayButtonContainer = null;
  }

  private destroyGooglePayButton(): void {
    this.googlePayButtonGeneration += 1;
    this.googlePayButtonTarget = null;
    this.removeGooglePayButton();
  }

  private startGooglePay(
    requestId: string,
    environment: 'TEST' | 'PRODUCTION',
    requestJson: string,
  ): void {
    this.clearGooglePayAttempt();
    // clearGooglePayAttempt restores the official button for a completed or
    // canceled attempt. This path is starting a new attempt, so keep it hidden
    // before launching Google Pay synchronously from the trusted click.
    if (this.googlePayButtonContainer) {
      this.googlePayButtonContainer.style.visibility = 'hidden';
      this.googlePayButtonContainer.style.pointerEvents = 'none';
    }
    let request: Record<string, unknown>;
    try {
      request = parseJsonRecord(requestJson);
    } catch {
      this.group.sendGooglePayError(
        this.componentId,
        requestId,
        'elements_google_pay_request_invalid',
        'Google Pay could not be opened because its request was invalid.',
      );
      return;
    }

    const PaymentsClient = (globalThis as any).google?.payments?.api?.PaymentsClient;
    if (!PaymentsClient || this.googlePayEnvironment !== environment) {
      this.group.sendGooglePayError(
        this.componentId,
        requestId,
        'elements_google_pay_unavailable',
        'Google Pay is not available in this browser.',
      );
      return;
    }

    this.googlePayRequestId = requestId;
    try {
      const callbackIntents = new Set(
        Array.isArray(request.callbackIntents)
          ? request.callbackIntents.filter((intent): intent is string => typeof intent === 'string')
          : [],
      );
      const paymentDataCallbacks: Record<
        string,
        (data: Record<string, unknown>) => Promise<unknown>
      > = {};
      if (
        callbackIntents.has('OFFER') ||
        callbackIntents.has('SHIPPING_ADDRESS') ||
        callbackIntents.has('SHIPPING_OPTION')
      ) {
        paymentDataCallbacks.onPaymentDataChanged = (data) =>
          this.requestGooglePayDataChanged(requestId, data);
      }
      if (callbackIntents.has('PAYMENT_AUTHORIZATION')) {
        paymentDataCallbacks.onPaymentAuthorized = (data) =>
          this.requestGooglePayAuthorization(requestId, data);
      }
      this.googlePayClient = this.createGooglePayClient(
        environment,
        Object.keys(paymentDataCallbacks).length > 0 ? paymentDataCallbacks : undefined,
      );
      // Keep the browser launch synchronous with the relayed click. Awaiting
      // script or merchant work here loses mobile popup/user-activation state.
      const popupRecovery = launchWithProviderPopupRecovery(
        () => this.googlePayClient.loadPaymentData(request),
        () => {
          if (this.googlePayRequestId !== requestId || this.destroyed) return;
          try {
            this.group.sendGooglePayCanceled(this.componentId, requestId);
          } finally {
            this.clearGooglePayAttempt(requestId);
          }
        },
      );
      this.googlePayPopupRecovery = popupRecovery;
      const payment = popupRecovery.result;
      void Promise.resolve(payment).then(
        (data: Record<string, unknown>) => {
          if (this.googlePayRequestId !== requestId || this.destroyed) return;
          this.clearGooglePayPopupRecovery();
          // Google has returned control to the merchant document at this
          // boundary. Keep blocking duplicate checkout interactions, but stop
          // telling the customer that the provider window is still open while
          // tokenization and a possible 3DS action are prepared.
          this.googlePayProcessingRequestId = requestId;
          this.group.markWalletAttemptProcessing(this.componentId, requestId, 'google_pay');
          this.group.sendGooglePayPaymentData(
            this.componentId,
            requestId,
            stringifyJsonRecord(data),
          );
        },
        (error: unknown) => {
          if (this.googlePayRequestId !== requestId || this.destroyed) return;
          try {
            if (isGooglePayCancellation(error)) {
              this.group.sendGooglePayCanceled(this.componentId, requestId);
            } else {
              this.group.sendGooglePayError(
                this.componentId,
                requestId,
                googlePayErrorCode(error),
                googlePayErrorMessage(error),
              );
            }
          } finally {
            this.clearGooglePayAttempt(requestId);
          }
        },
      );
    } catch (error) {
      this.group.sendGooglePayError(
        this.componentId,
        requestId,
        googlePayErrorCode(error),
        googlePayErrorMessage(error),
      );
      this.clearGooglePayAttempt(requestId);
    }
  }

  private createGooglePayClient(
    environment: 'TEST' | 'PRODUCTION',
    paymentDataCallbacks?: Record<string, (data: Record<string, unknown>) => Promise<unknown>>,
  ): any {
    const PaymentsClient = (globalThis as any).google?.payments?.api?.PaymentsClient;
    if (!PaymentsClient) throw new Error('Google Pay is unavailable.');
    return new PaymentsClient({
      environment,
      ...(paymentDataCallbacks ? { paymentDataCallbacks } : {}),
    });
  }

  private requestGooglePayDataChanged(
    requestId: string,
    data: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    if (this.googlePayRequestId !== requestId) {
      return Promise.resolve(
        googlePayCallbackError('OTHER_ERROR', 'The payment attempt is stale.'),
      );
    }
    const updateId = generateId();
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.googlePayUpdates.delete(updateId);
        resolve(googlePayCallbackError('OTHER_ERROR', 'The merchant update timed out.'));
      }, EXPRESS_CHECKOUT_ELEMENT_POLICY.merchantWalletUpdateTimeoutMs);
      this.googlePayUpdates.set(updateId, { requestId, resolve, timeout });
      try {
        this.group.sendGooglePayDataChanged(
          this.componentId,
          requestId,
          updateId,
          stringifyJsonRecord(data),
        );
      } catch {
        clearTimeout(timeout);
        this.googlePayUpdates.delete(updateId);
        resolve(googlePayCallbackError('OTHER_ERROR', 'The merchant update was invalid.'));
      }
    });
  }

  private requestGooglePayAuthorization(
    requestId: string,
    data: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    if (this.googlePayRequestId !== requestId || this.googlePayAuthorization) {
      return Promise.resolve(googlePayAuthorizationError('The payment attempt is stale.'));
    }
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.googlePayAuthorization = null;
        resolve(googlePayAuthorizationError('The merchant authorization timed out.'));
      }, EXPRESS_CHECKOUT_ELEMENT_POLICY.merchantWalletUpdateTimeoutMs);
      this.googlePayAuthorization = { requestId, resolve, timeout };
      try {
        this.group.sendGooglePayAuthorized(this.componentId, requestId, stringifyJsonRecord(data));
      } catch {
        clearTimeout(timeout);
        this.googlePayAuthorization = null;
        resolve(googlePayAuthorizationError('The merchant authorization was invalid.'));
      }
    });
  }

  private resolveGooglePayDataChanged(
    requestId: string,
    updateId: string,
    resultJson: string,
  ): void {
    const pending = this.googlePayUpdates.get(updateId);
    if (!pending || pending.requestId !== requestId || this.googlePayRequestId !== requestId)
      return;
    this.googlePayUpdates.delete(updateId);
    clearTimeout(pending.timeout);
    try {
      pending.resolve(parseJsonRecord(resultJson));
    } catch {
      pending.resolve(googlePayCallbackError('OTHER_ERROR', 'The merchant update was invalid.'));
    }
  }

  private resolveGooglePayAuthorization(requestId: string, resultJson: string): void {
    const pending = this.googlePayAuthorization;
    if (!pending || pending.requestId !== requestId || this.googlePayRequestId !== requestId)
      return;
    this.googlePayAuthorization = null;
    clearTimeout(pending.timeout);
    try {
      pending.resolve(parseJsonRecord(resultJson));
    } catch {
      pending.resolve(googlePayAuthorizationError('The merchant authorization was invalid.'));
    }
  }

  private clearGooglePayAttempt(requestId?: string): void {
    if (requestId && this.googlePayRequestId !== requestId) return;
    this.clearGooglePayPopupRecovery();
    this.googlePayRequestId = null;
    this.googlePayProcessingRequestId = null;
    for (const pending of this.googlePayUpdates.values()) {
      clearTimeout(pending.timeout);
      pending.resolve(googlePayCallbackError('OTHER_ERROR', 'The payment attempt was canceled.'));
    }
    this.googlePayUpdates.clear();
    if (this.googlePayAuthorization) {
      clearTimeout(this.googlePayAuthorization.timeout);
      this.googlePayAuthorization.resolve(
        googlePayAuthorizationError('The payment attempt was canceled.'),
      );
      this.googlePayAuthorization = null;
    }
    this.positionGooglePayButton();
  }

  private clearGooglePayPopupRecovery(): void {
    this.googlePayPopupRecovery?.stop();
    this.googlePayPopupRecovery = null;
  }

  private startApplePay(requestId: string, request: ElementsApplePayPaymentRequest): void {
    this.abortApplePay();
    const ApplePayAPI = (window as any).ApplePaySession;
    if (!ApplePayAPI || !this.canMakeApplePayPayments()) {
      this.group.sendApplePayCanceled(this.componentId, requestId);
      this.emitter.emit('error', {
        code: 'elements_apple_pay_unavailable',
        message: 'Apple Pay is not available in this browser.',
        recoverable: true,
      });
      return;
    }

    let session: any;
    try {
      const version = selectApplePaySessionVersion(ApplePayAPI);
      session = new ApplePayAPI(version, this.prepareApplePayRequest(request));
    } catch {
      this.group.sendApplePayCanceled(this.componentId, requestId);
      this.emitter.emit('error', {
        code: 'elements_apple_pay_start_failed',
        message: 'Apple Pay could not be opened.',
        recoverable: true,
      });
      return;
    }

    this.applePaySession = session;
    session.onvalidatemerchant = (event: { validationURL: string }) => {
      this.applePayTimeout = setTimeout(() => {
        this.abortApplePay();
        this.group.sendApplePayCanceled(this.componentId, requestId);
      }, APPLE_PAY_BRIDGE_POLICY.merchantValidationTimeoutMs);
      this.group.sendApplePayValidateMerchant(this.componentId, requestId, event.validationURL);
    };
    session.onpaymentauthorized = (event: { payment: Record<string, unknown> }) => {
      this.group.sendApplePayAuthorized(this.componentId, requestId, event.payment);
    };
    session.onshippingcontactselected = (event: { shippingContact: unknown }) => {
      const address = appleShippingAddress(event.shippingContact);
      if (!address) {
        this.completeApplePayShippingContactSelection(session, request, {
          accepted: false,
          amount: this.group.getUpdateSnapshot().options.amount,
          currency: this.group.getUpdateSnapshot().options.currency,
        });
        return;
      }
      this.emitShippingAddressChange(requestId, generateId(), 'apple_pay', address, (result) =>
        this.completeApplePayShippingContactSelection(session, request, result),
      );
    };
    session.onshippingmethodselected = (event: { shippingMethod?: { identifier?: string } }) => {
      const selected = (this.activeOptions?.shippingRates ?? this.options.shippingRates ?? []).find(
        (rate) => rate.id === event.shippingMethod?.identifier,
      );
      if (!selected) {
        this.completeApplePayShippingMethodSelection(session, request, {
          accepted: false,
          amount: this.group.getUpdateSnapshot().options.amount,
          currency: this.group.getUpdateSnapshot().options.currency,
        });
        return;
      }
      this.emitShippingRateChange(requestId, generateId(), 'apple_pay', selected, (result) =>
        this.completeApplePayShippingMethodSelection(session, request, result),
      );
    };
    session.oncancel = () => {
      this.clearApplePayTimeout();
      this.applePaySession = null;
      this.activeOptions = null;
      this.group.sendApplePayCanceled(this.componentId, requestId);
    };
    try {
      session.begin();
    } catch {
      this.abortApplePay();
      this.group.sendApplePayCanceled(this.componentId, requestId);
    }
  }

  private completeApplePay(success: boolean): void {
    if (!this.applePaySession) return;
    const session = this.applePaySession;
    const ApplePayAPI = (window as any).ApplePaySession;
    this.clearApplePayTimeout();
    this.applePaySession = null;
    try {
      session.completePayment(success ? ApplePayAPI.STATUS_SUCCESS : ApplePayAPI.STATUS_FAILURE);
    } catch {
      try {
        session.abort();
      } catch {}
    }
  }

  private abortApplePay(): void {
    this.clearApplePayTimeout();
    try {
      this.applePaySession?.abort();
    } catch {}
    this.applePaySession = null;
  }

  private walletUpdateSettlement(
    accepted: boolean,
    update?: ExpressCheckoutShippingUpdate,
  ): WalletUpdateSettlementResult {
    const snapshot = this.group.getUpdateSnapshot().options;
    return {
      accepted,
      ...(update ? { update } : {}),
      amount: update?.amount ?? snapshot.amount,
      currency: snapshot.currency,
    };
  }

  private prepareApplePayRequest(
    request: ElementsApplePayPaymentRequest,
  ): ElementsApplePayPaymentRequest {
    const snapshot = this.group.getUpdateSnapshot().options;
    const options = this.activeOptions ?? this.options;
    const currency = snapshot.currency;
    return {
      ...request,
      currencyCode: currency.toUpperCase(),
      total: applePayTotal(request.total.label, snapshot.amount, currency),
      ...(options.lineItems?.length
        ? { lineItems: applePayLineItems(options.lineItems, currency) }
        : { lineItems: undefined }),
      ...(options.shippingAddressRequired
        ? {
            requiredShippingContactFields: [
              'name' as const,
              'postalAddress' as const,
              ...(options.emailRequired ? (['email'] as const) : []),
              ...(options.phoneNumberRequired ? (['phone'] as const) : []),
            ],
          }
        : { requiredShippingContactFields: undefined }),
      ...(options.shippingAddressRequired && options.shippingRates?.length
        ? { shippingMethods: applePayShippingMethods(options.shippingRates, currency) }
        : { shippingMethods: undefined }),
    };
  }

  private completeApplePayShippingContactSelection(
    session: any,
    request: ElementsApplePayPaymentRequest,
    result: WalletUpdateSettlementResult,
  ): void {
    if (session !== this.applePaySession) return;
    const rates = result.update?.shippingRates ?? this.activeOptions?.shippingRates ?? [];
    const ApplePayErrorAPI = (window as any).ApplePayError;
    try {
      session.completeShippingContactSelection({
        newShippingMethods: result.accepted ? applePayShippingMethods(rates, result.currency) : [],
        newTotal: applePayTotal(request.total.label, result.amount, result.currency),
        newLineItems: applePayLineItems(
          result.update?.lineItems ?? this.activeOptions?.lineItems,
          result.currency,
        ),
        ...(!result.accepted && ApplePayErrorAPI
          ? {
              errors: [
                new ApplePayErrorAPI(
                  'shippingContactInvalid',
                  undefined,
                  'This address is not available for shipping.',
                ),
              ],
            }
          : {}),
      });
    } catch {
      const requestId = this.activeAttempt?.requestId;
      if (requestId) this.group.sendApplePayCanceled(this.componentId, requestId);
      this.abortApplePay();
    }
  }

  private completeApplePayShippingMethodSelection(
    session: any,
    request: ElementsApplePayPaymentRequest,
    result: WalletUpdateSettlementResult,
  ): void {
    if (session !== this.applePaySession) return;
    try {
      session.completeShippingMethodSelection({
        newTotal: applePayTotal(request.total.label, result.amount, result.currency),
        newLineItems: applePayLineItems(
          result.update?.lineItems ?? this.activeOptions?.lineItems,
          result.currency,
        ),
      });
    } catch {
      const requestId = this.activeAttempt?.requestId;
      if (requestId) this.group.sendApplePayCanceled(this.componentId, requestId);
      this.abortApplePay();
    }
  }

  private clearApplePayTimeout(): void {
    if (!this.applePayTimeout) return;
    clearTimeout(this.applePayTimeout);
    this.applePayTimeout = null;
  }
}

const OPTION_KEYS = new Set([
  'paymentMethods',
  'layout',
  'buttonType',
  'buttonTheme',
  'buttonHeight',
  'buttonRadius',
  'emailRequired',
  'phoneNumberRequired',
  'billingAddressRequired',
  'shippingAddressRequired',
  'allowedShippingCountries',
  'lineItems',
  'shippingRates',
]);

function developerDisplayOptions(
  options: ExpressCheckoutElementOptions,
): DeveloperAssistantComponentDisplayOptions {
  return structuredCloneSafe({
    ...(options.paymentMethods ? { paymentMethods: options.paymentMethods } : {}),
    ...(options.layout ? { layout: options.layout } : {}),
    ...(options.buttonType ? { buttonType: options.buttonType } : {}),
    ...(options.buttonTheme ? { buttonTheme: options.buttonTheme } : {}),
    ...(options.buttonHeight !== undefined ? { buttonHeight: options.buttonHeight } : {}),
    ...(options.buttonRadius !== undefined ? { buttonRadius: options.buttonRadius } : {}),
  });
}

function mergeDeveloperDisplayOptions(
  current: ExpressCheckoutElementOptions,
  update: DeveloperAssistantComponentDisplayOptions,
): ExpressCheckoutElementOptions {
  return {
    ...current,
    ...update,
    ...(update.paymentMethods
      ? { paymentMethods: { ...current.paymentMethods, ...update.paymentMethods } }
      : {}),
    ...(update.layout ? { layout: { ...current.layout, ...update.layout } } : {}),
    ...(update.buttonType ? { buttonType: { ...current.buttonType, ...update.buttonType } } : {}),
    ...(update.buttonTheme
      ? { buttonTheme: { ...current.buttonTheme, ...update.buttonTheme } }
      : {}),
  };
}

function appearancePreviewRefusedError(): Error & {
  code: 'elements_appearance_preview_refused';
} {
  return Object.assign(
    new Error(
      'Express Checkout preview is unavailable while payment validation or a wallet attempt is active.',
    ),
    { code: 'elements_appearance_preview_refused' as const },
  );
}

function skipAppearanceRecovery(error: Error): Error {
  return Object.assign(error, { skipAppearanceRecovery: true as const });
}

function shouldSkipAppearanceRecovery(error: unknown): boolean {
  return (
    (isRecord(error) && error.skipAppearanceRecovery === true) ||
    (isRecord(error) && error.code === 'elements_appearance_preview_refused')
  );
}

// WooCommerce's complete country catalogue contains the ISO 3166-1 entries
// plus the widely used XK code for Kosovo (250 values today). Keep a small,
// explicit protocol bound without rejecting that authoritative catalogue.
const MAX_ALLOWED_SHIPPING_COUNTRIES = 256;

function validateOptions(options: ExpressCheckoutElementOptions): ExpressCheckoutElementOptions {
  const rawOptions: unknown = options;
  if (!isRecord(rawOptions) || Object.keys(rawOptions).some((key) => !OPTION_KEYS.has(key))) {
    throw new Error('[Paypercut Elements] Express Checkout options are invalid.');
  }
  if (options.buttonHeight !== undefined) {
    if (
      typeof options.buttonHeight !== 'number' ||
      !Number.isSafeInteger(options.buttonHeight) ||
      options.buttonHeight < EXPRESS_CHECKOUT_ELEMENT_POLICY.buttonMinimumHeightCssPixels ||
      options.buttonHeight > EXPRESS_CHECKOUT_ELEMENT_POLICY.buttonMaximumHeightCssPixels
    ) {
      throw new Error('[Paypercut Elements] Express Checkout button height must be 40–64 pixels.');
    }
  }
  if (options.buttonRadius !== undefined) {
    if (
      typeof options.buttonRadius !== 'number' ||
      !Number.isSafeInteger(options.buttonRadius) ||
      options.buttonRadius < 0 ||
      options.buttonRadius > EXPRESS_CHECKOUT_ELEMENT_POLICY.buttonMaximumRadiusCssPixels
    ) {
      throw new Error('[Paypercut Elements] Express Checkout button radius must be 0–24 pixels.');
    }
  }
  if (options.layout) {
    if (typeof options.layout !== 'object' || Array.isArray(options.layout)) throw invalidOptions();
    for (const field of ['maxColumns', 'maxRows'] as const) {
      const value = options.layout[field];
      if (
        value !== undefined &&
        (typeof value !== 'number' ||
          !Number.isSafeInteger(value) ||
          value < 1 ||
          value > EXPRESS_CHECKOUT_ELEMENT_POLICY.layoutMaximumTrackCount)
      ) {
        throw invalidOptions();
      }
    }
    if (
      options.layout.overflow !== undefined &&
      !['auto', 'never'].includes(options.layout.overflow)
    ) {
      throw invalidOptions();
    }
  }
  if (options.paymentMethods) {
    for (const value of Object.values(options.paymentMethods)) {
      if (value !== undefined && !['auto', 'always', 'never'].includes(value))
        throw invalidOptions();
    }
  }
  if (options.buttonType) {
    if (
      options.buttonType.applePay !== undefined &&
      !['plain', 'buy', 'check-out', 'donate', 'book', 'subscribe'].includes(
        options.buttonType.applePay,
      )
    ) {
      throw invalidOptions();
    }
    if (
      options.buttonType.googlePay !== undefined &&
      !['plain', 'pay', 'buy', 'checkout', 'donate', 'book', 'subscribe'].includes(
        options.buttonType.googlePay,
      )
    ) {
      throw invalidOptions();
    }
  }
  if (options.buttonTheme) {
    if (
      options.buttonTheme.applePay !== undefined &&
      !['black', 'white', 'white-outline'].includes(options.buttonTheme.applePay)
    ) {
      throw invalidOptions();
    }
    if (
      options.buttonTheme.googlePay !== undefined &&
      !['black', 'white'].includes(options.buttonTheme.googlePay)
    ) {
      throw invalidOptions();
    }
  }
  if (options.allowedShippingCountries) {
    if (
      !Array.isArray(options.allowedShippingCountries) ||
      options.allowedShippingCountries.length > MAX_ALLOWED_SHIPPING_COUNTRIES ||
      options.allowedShippingCountries.some((country) => !/^[A-Za-z]{2}$/.test(country))
    ) {
      throw invalidOptions();
    }
  }
  for (const flag of [
    options.emailRequired,
    options.phoneNumberRequired,
    options.billingAddressRequired,
    options.shippingAddressRequired,
  ]) {
    if (flag !== undefined && typeof flag !== 'boolean') throw invalidOptions();
  }
  validateRows(options.lineItems, 'name');
  validateRows(options.shippingRates, 'displayName', true);
  return structuredCloneSafe({
    ...options,
    ...(options.allowedShippingCountries
      ? {
          allowedShippingCountries: options.allowedShippingCountries.map((country) =>
            country.toUpperCase(),
          ),
        }
      : {}),
  });
}

function validateRows(rows: unknown, labelKey: string, requireId = false): void {
  if (rows === undefined) return;
  if (!Array.isArray(rows) || rows.length > EXPRESS_CHECKOUT_ELEMENT_POLICY.optionRowCount) {
    throw invalidOptions();
  }
  for (const row of rows) {
    if (
      !isRecord(row) ||
      typeof row.amount !== 'number' ||
      !Number.isSafeInteger(row.amount) ||
      row.amount < 0 ||
      !isCleanString(row[labelKey], EXPRESS_CHECKOUT_ELEMENT_POLICY.optionRowLabelCharacters) ||
      (requireId && !isCleanString(row.id, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters))
    ) {
      throw invalidOptions();
    }
  }
}

function validateClickResolveOptions(
  options?: ExpressCheckoutClickResolveOptions,
): ExpressCheckoutClickResolveOptions | undefined {
  if (options === undefined) return undefined;
  const allowed = new Set([
    'amount',
    'lineItems',
    'emailRequired',
    'phoneNumberRequired',
    'billingAddressRequired',
    'shippingAddressRequired',
    'allowedShippingCountries',
    'shippingRates',
  ]);
  if (!isRecord(options) || Object.keys(options).some((key) => !allowed.has(key))) {
    throw invalidOptions();
  }
  if (
    options.amount !== undefined &&
    (typeof options.amount !== 'number' ||
      !Number.isSafeInteger(options.amount) ||
      options.amount < 0)
  ) {
    throw invalidOptions();
  }
  validateRows(options.lineItems, 'name');
  validateRows(options.shippingRates, 'displayName', true);
  for (const flag of [
    options.emailRequired,
    options.phoneNumberRequired,
    options.billingAddressRequired,
    options.shippingAddressRequired,
  ]) {
    if (flag !== undefined && typeof flag !== 'boolean') throw invalidOptions();
  }
  if (
    options.allowedShippingCountries !== undefined &&
    (!Array.isArray(options.allowedShippingCountries) ||
      options.allowedShippingCountries.length > MAX_ALLOWED_SHIPPING_COUNTRIES ||
      options.allowedShippingCountries.some((country) => !/^[A-Za-z]{2}$/.test(country)))
  ) {
    throw invalidOptions();
  }
  return structuredCloneSafe({
    ...options,
    ...(options.allowedShippingCountries
      ? {
          allowedShippingCountries: options.allowedShippingCountries.map((country) =>
            country.toUpperCase(),
          ),
        }
      : {}),
  });
}

function validateElementUpdateOptions(
  options: ExpressCheckoutElementUpdateOptions,
): ExpressCheckoutElementUpdateOptions {
  if (!isRecord(options) || Object.keys(options).length === 0 || 'amount' in options) {
    throw new Error(
      '[Paypercut Elements] Express Checkout update options must contain checkout details.',
    );
  }
  return validateClickResolveOptions(options)!;
}

function validateShippingUpdate(
  update?: ExpressCheckoutShippingUpdate,
): ExpressCheckoutShippingUpdate | undefined {
  if (update === undefined) return undefined;
  if (
    !isRecord(update) ||
    Object.keys(update).some((key) => !['amount', 'lineItems', 'shippingRates'].includes(key))
  ) {
    throw invalidOptions();
  }
  const amount = update.amount;
  if (
    amount !== undefined &&
    (typeof amount !== 'number' || !Number.isSafeInteger(amount) || amount < 0)
  ) {
    throw invalidOptions();
  }
  validateRows(update.lineItems, 'name');
  validateRows(update.shippingRates, 'displayName', true);
  return structuredCloneSafe(update);
}

function currencyDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).resolvedOptions().maximumFractionDigits ??
      EXPRESS_CHECKOUT_ELEMENT_POLICY.defaultCurrencyFractionDigits
    );
  } catch {
    return EXPRESS_CHECKOUT_ELEMENT_POLICY.defaultCurrencyFractionDigits;
  }
}

function majorAmount(amount: number, currency: string): string {
  const digits = currencyDigits(currency);
  return (amount / EXPRESS_CHECKOUT_ELEMENT_POLICY.decimalRadix ** digits).toFixed(digits);
}

function applePayTotal(label: string, amount: number, currency: string) {
  return {
    label,
    amount: majorAmount(amount, currency),
    type: 'final' as const,
  };
}

function applePayLineItems(
  lineItems: Array<{ name: string; amount: number }> | undefined,
  currency: string,
) {
  return (lineItems ?? []).map((item) => ({
    label: item.name,
    amount: majorAmount(item.amount, currency),
    type: 'final' as const,
  }));
}

function applePayShippingMethods(
  rates: Array<{ id: string; displayName: string; amount: number }>,
  currency: string,
) {
  return rates.map((rate) => ({
    identifier: rate.id,
    label: rate.displayName,
    detail: '',
    amount: majorAmount(rate.amount, currency),
  }));
}

function appleShippingAddress(
  value: unknown,
): ExpressCheckoutShippingAddressChangeEvent['address'] | undefined {
  if (!isRecord(value)) return undefined;
  const addressLine = Array.isArray(value.addressLines)
    ? value.addressLines.filter((line): line is string => typeof line === 'string' && line !== '')
    : [];
  const recipient = [value.givenName, value.familyName]
    .filter((part): part is string => typeof part === 'string' && part !== '')
    .join(' ');
  const address = {
    ...(recipient ? { recipient } : {}),
    ...(addressLine.length ? { addressLine } : {}),
    ...(typeof value.locality === 'string' && value.locality ? { city: value.locality } : {}),
    ...(typeof value.administrativeArea === 'string' && value.administrativeArea
      ? { state: value.administrativeArea }
      : {}),
    ...(typeof value.postalCode === 'string' && value.postalCode
      ? { postalCode: value.postalCode }
      : {}),
    ...(typeof value.countryCode === 'string' && value.countryCode
      ? { country: value.countryCode.toUpperCase() }
      : {}),
  };
  return Object.keys(address).length ? address : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJsonRecord(value: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(value);
  if (!isRecord(parsed)) throw new Error('Expected a JSON object.');
  return parsed;
}

function stringifyJsonRecord(value: unknown): string {
  if (!isRecord(value)) throw new Error('Expected an object.');
  return JSON.stringify(value);
}

function googlePayCallbackError(reason: string, message: string): Record<string, unknown> {
  return { error: { reason, message, intent: 'PAYMENT_AUTHORIZATION' } };
}

function googlePayAuthorizationError(message: string): Record<string, unknown> {
  return {
    transactionState: 'ERROR',
    error: { intent: 'PAYMENT_AUTHORIZATION', reason: 'PAYMENT_DATA_INVALID', message },
  };
}

function isGooglePayCancellation(error: unknown): boolean {
  return isRecord(error) && (error.statusCode === 'CANCELED' || error.code === 'CANCELED');
}

function googlePayErrorCode(error: unknown): string {
  if (isRecord(error)) {
    const value = error.statusCode ?? error.code;
    if (isBoundedProtocolString(value, ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters)) {
      return value;
    }
  }
  return 'elements_google_pay_failed';
}

function googlePayErrorMessage(error: unknown): string {
  if (isRecord(error)) {
    const value = error.statusMessage ?? error.message;
    if (isBoundedProtocolString(value, ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters)) {
      return value;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message.slice(0, ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters);
  }
  return 'Google Pay could not be completed.';
}

const isCleanString = isBoundedProtocolString;

function invalidOptions(): Error {
  return new Error('[Paypercut Elements] Express Checkout options are invalid.');
}

function structuredCloneSafe<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function resolveContainer(target: string | HTMLElement): HTMLElement {
  const container = typeof target === 'string' ? document.querySelector(target) : target;
  if (!(container instanceof HTMLElement)) {
    throw new Error(`[Paypercut Elements] Container not found: ${String(target)}`);
  }
  return container;
}
